import React, { useState } from 'react';
import { Box, IconButton, Typography, Collapse, TextField, Stack, FormHelperText } from '@mui/material';
import {
  DragIndicator as DragHandleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { translate as t } from 'hooks/useTranslations';
import {
  PolicyHeaderOrderItem,
  PolicyHeaderFieldType,
  PolicyHeaderIssuingAuthorityType,
  Attachment,
} from '../../_types/manuals/draft';
import AttachmentSelectorInline from './AttachmentSelectorInline';

interface SortableHeaderOrderItemProps {
  item: PolicyHeaderOrderItem;
  displayName: string;
  onToggleVisibility: (orderType: PolicyHeaderFieldType) => void;
  manualId?: string;
  issuingAuthorityType?: PolicyHeaderIssuingAuthorityType;
  // Default value props
  agencyLogoId?: string;
  issuingAuthoritySignatureId?: string;
  issuingAuthorityName?: string;
  customFields?: Array<{ header: string; value: string; }>;
  attachments?: Attachment[];
  onUpdateAgencyLogo?: (attachmentId: string) => void;
  onUpdateIssuingAuthoritySignature?: (attachmentId: string) => void;
  onUpdateIssuingAuthorityName?: (name: string) => void;
  onUpdateCustomField?: (index: number, field: { header: string; value: string; }) => void;
  onAttachmentUploaded?: (newAttachment: any) => void;
}

const SortableHeaderOrderItem: React.FC<SortableHeaderOrderItemProps> = ({
  item,
  displayName,
  onToggleVisibility,
  manualId,
  issuingAuthorityType = 'SIGNATURE_AND_NAME',
  agencyLogoId,
  issuingAuthoritySignatureId,
  issuingAuthorityName,
  customFields,
  attachments,
  onUpdateAgencyLogo,
  onUpdateIssuingAuthoritySignature,
  onUpdateIssuingAuthorityName,
  onUpdateCustomField,
  onAttachmentUploaded,
}) => {
  const [expanded, setExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.type,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : item.state === 'DISABLED' ? 0.5 : 1,
  };

  // Determine if this field has default value configuration
  const hasDefaultValueConfig = ['AGENCY_LOGO', 'ISSUING_AUTHORITY', 'CUSTOM1', 'CUSTOM2'].includes(item.type);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // Get custom field index for CUSTOM1/CUSTOM2
  const customFieldIndex = item.type === 'CUSTOM1' ? 0 : item.type === 'CUSTOM2' ? 1 : -1;
  const currentCustomField = customFieldIndex >= 0 ? customFields?.[customFieldIndex] : null;

  // Check if toggle should be disabled for custom fields without title
  const isToggleDisabled =
    (item.type === 'CUSTOM1' && !customFields?.[0]?.header) ||
    (item.type === 'CUSTOM2' && !customFields?.[1]?.header);

  const renderDefaultValueConfig = () => {
    if (!manualId) return null;

    switch (item.type) {
      case 'AGENCY_LOGO':
        return (
          <Box sx={{ mt: 2 }}>
            <AttachmentSelectorInline
              manualId={manualId}
              attachmentId={agencyLogoId}
              onSelectAttachment={(id) => onUpdateAgencyLogo?.(id)}
              label={t('draftManuals.manualProperties.policyHeaders.order.agencyLogoLabel')}
              acceptImageOnly={true}
              attachments={attachments}
            />
          </Box>
        );

      case 'ISSUING_AUTHORITY':
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* Show signature image field based on issuingAuthorityType */}
            {(issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'SIGNATURE_ONLY') && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {t('draftManuals.manualProperties.policyHeaders.order.issuingAuthoritySignatureImage')}
                </Typography>
                <AttachmentSelectorInline
                  manualId={manualId}
                  attachmentId={issuingAuthoritySignatureId}
                  onSelectAttachment={(id) => onUpdateIssuingAuthoritySignature?.(id)}
                  label={t('draftManuals.manualProperties.policyHeaders.order.signatureImageLabel')}
                  acceptImageOnly={true}
                  attachments={attachments}
                  onAttachmentUploaded={onAttachmentUploaded}
                />
              </Box>
            )}

            {/* Show name text field based on issuingAuthorityType */}
            {(issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'NAME_ONLY') && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {t('draftManuals.manualProperties.policyHeaders.order.issuingAuthorityNameLabel')}
                </Typography>
                <TextField
                  sx={{ p: 0 }}
                  fullWidth
                  size='small'
                  placeholder={t('draftManuals.manualProperties.policyHeaders.order.enterIssuingAuthorityNamePlaceholder')}
                  value={issuingAuthorityName || ''}
                  onChange={(e) => onUpdateIssuingAuthorityName?.(e.target.value)}
                />
              </Box>
            )}
          </Stack>
        );

      case 'CUSTOM1':
      case 'CUSTOM2':
        if (currentCustomField && onUpdateCustomField) {
          const fieldIndex = customFieldIndex;
          const hasNoTitle = !currentCustomField.header;

          return (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {t('draftManuals.manualProperties.policyHeaders.order.customFieldTitleLabel')}
                </Typography>
                <TextField
                  sx={{ p: 0 }}
                  fullWidth
                  size='small'
                  placeholder={t('draftManuals.manualProperties.policyHeaders.order.enterFieldTitlePlaceholder')}
                  value={currentCustomField.header}
                  onChange={(e) => {
                    const newField = { ...currentCustomField, header: e.target.value };
                    onUpdateCustomField(fieldIndex, newField);
                  }}
                  error={hasNoTitle && item.state === 'ENABLED'}
                  helperText={hasNoTitle ? t('draftManuals.manualProperties.policyHeaders.order.titleRequiredError') : ''}
                />
              </Box>

              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  {t('draftManuals.manualProperties.policyHeaders.order.defaultValueLabel')}
                </Typography>
                <TextField
                  sx={{ p: 0 }}
                  fullWidth
                  size='small'
                  placeholder={t('draftManuals.manualProperties.policyHeaders.order.enterDefaultValuePlaceholder')}
                  value={currentCustomField.value}
                  onChange={(e) => {
                    const newField = { ...currentCustomField, value: e.target.value };
                    onUpdateCustomField(fieldIndex, newField);
                  }}
                />
              </Box>
            </Stack>
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {/* Main row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
        }}
      >
        <IconButton
          {...attributes}
          {...listeners}
          size='small'
          sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        >
          <DragHandleIcon />
        </IconButton>
        <Typography variant='body1' sx={{ flex: 1 }}>
          {displayName}
        </Typography>

        {/* Expand/collapse button for fields with default values */}
        {hasDefaultValueConfig && (
          <IconButton size='small' onClick={handleToggleExpand}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        {/* Toggle visibility button */}
        <IconButton
          size='small'
          onClick={() => onToggleVisibility(item.type)}
          color={item.state === 'ENABLED' ? 'primary' : 'default'}
          disabled={isToggleDisabled}
        >
          {item.state === 'ENABLED' ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
      </Box>

      {/* Expandable default value configuration */}
      {hasDefaultValueConfig && (
        <Collapse in={expanded}>
          <Box sx={{ px: 2, pb: 2 }}>{renderDefaultValueConfig()}</Box>
        </Collapse>
      )}
    </Box>
  );
};

export default SortableHeaderOrderItem;
